import { type Dispatch, type SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import React from 'react';

import classNames from 'classnames';
import throttle from 'lodash/throttle';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_LANGUAGE } from '../../i18n/constants';
import { getUserLanguage } from '../../i18n/helpers';
import type { Category } from '../../stores/categories/CategoriesStore';
import type { Game } from '../../stores/game/types';
import { useStores } from '../../stores/useStores';
import Chevron from '../SVGIcons/Chevron/Chevron';

import { CarouselBlock } from './CarouselBlock';
import styles from './GamesCarousel.module.scss';
import { isJackpotGame } from './isJackpotGame';
import { seenGamesLobbyListener } from './seenGamesImpression';

type Chunks = Map<number, Set<Game>>;

function updateChunks(games: Game[], chunks: Chunks, chunkSize: number): Chunks {
  for (let i = 0, chunk = 0; i < games.length; i += chunkSize, chunk++) {
    chunks.set(chunk, new Set(games.slice(i, i + chunkSize)));
  }
  return chunks;
}

export const GamesInfiniteCarousel = observer(function GamesCarousel(props: {
  id: Category['masterGameSubCategoryId'];
  onboardingRow: boolean;
  setReferenceElement: Dispatch<SetStateAction<HTMLElement | null>>;
  carouselClassName?: string;
  carouselLeftArrowClassName?: string;
  carouselRightArrowClassName?: string;
}) {
  const { carouselClassName, carouselLeftArrowClassName, carouselRightArrowClassName } = props;
  const { i18n } = useTranslation();
  const language = getUserLanguage(i18n.language);
  const { categoriesStore } = useStores();
  const category = categoriesStore.categoriesRegistry[props.id];

  // could be taken from LaunchDarkly
  const chunkSize = window.innerWidth > 768 ? 7 : 3;

  const lastReflectedLabelCfgUpdate = useRef(categoriesStore.lastLabelConfigsUpdateTs);
  const carouselRef = useRef<HTMLDivElement>(null);
  const position = useRef(0);
  const pagesFetched = useRef(categoriesStore.pageByCategory[props.id]);
  const chunks = useRef(updateChunks(category.subCategoryGames ?? [], new Map(), chunkSize));

  const [visibleChunks, setVisibleChunks] = useState(Array.from(chunks.current.keys()));

  // Click on arrow button
  const scrollCarousel = useCallback((scrollOffset: number) => {
    const $el = carouselRef.current!;
    $el.scrollTo({ left: $el.scrollLeft + scrollOffset, behavior: 'smooth' });
  }, []);

  // On category changes, we should recreate the games chunks
  useEffect(() => {
    if (
      !categoriesStore.loading.get('pagination') &&
      (pagesFetched.current !== categoriesStore.pageByCategory[props.id] ||
        lastReflectedLabelCfgUpdate.current !== categoriesStore.lastLabelConfigsUpdateTs)
    ) {
      pagesFetched.current = categoriesStore.pageByCategory[props.id];
      lastReflectedLabelCfgUpdate.current = categoriesStore.lastLabelConfigsUpdateTs;
      chunks.current = updateChunks(
        categoriesStore.categoriesRegistry[props.id]?.subCategoryGames ?? [],
        chunks.current,
        chunkSize,
      );
      setVisibleChunks(Array.from(chunks.current.keys()));
    }
    // We should react only on loading changes (small speed hack)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriesStore.loading.get('pagination'), props.id, categoriesStore.lastLabelConfigsUpdateTs]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onScroll = useCallback(
    throttle((ev: React.UIEvent<HTMLDivElement>) => {
      seenGamesLobbyListener();
      const $el = ev.target as HTMLDivElement;
      const { scrollLeft, scrollWidth, clientWidth } = $el;
      const $wrapper = $el.parentNode as HTMLDivElement;
      if (scrollLeft < 10) {
        $wrapper.classList.add(styles.isStart);
      } else {
        $wrapper.classList.remove(styles.isStart);
      }
      if (scrollLeft > position.current && scrollLeft + clientWidth >= scrollWidth - 200) {
        position.current = scrollLeft;
        const categoryById = categoriesStore.categoriesRegistry[props.id];
        if (categoryById.hasMoreGames && !categoriesStore.loading.get('pagination')) {
          void categoriesStore.getNextGames(props.id);
          return;
        }
        setVisibleChunks((state) => {
          const nextChunk = state.length % chunks.current.size;
          return [...state, nextChunk];
        });
      }
    }, 100),
    [categoriesStore, props.id],
  );

  const firstChunk = chunks.current.get(0);
  // The whole category, not per chunk: a chunk holds only 3–7 tiles, and judging each on its own
  // would leave one block of the row out of line with the next.
  const rowHasJackpot = (category.subCategoryGames ?? []).some(isJackpotGame);

  return (
    <div
      className={classNames(styles.wrapper, carouselClassName, {
        [styles.wrapperOnboarding]: props.onboardingRow,
        [styles.isStart]: true,
      })}
    >
      {firstChunk && firstChunk.size > 6 && (
        <button
          className={classNames(styles.arrow, styles.arrowLeft, carouselLeftArrowClassName)}
          onClick={() => scrollCarousel(-300)}
        >
          <Chevron direction="left" />
        </button>
      )}
      <div
        ref={carouselRef}
        className={classNames(styles.carousel, {
          [styles.carouselOnboarding]: props.onboardingRow,
        })}
        onScroll={onScroll}
      >
        {visibleChunks.map((chunk, idx) => (
          <React.Fragment key={chunk + idx}>
            <CarouselBlock
              games={chunks.current.get(chunk)!}
              onboardingRow={props.onboardingRow && idx === 0}
              rowHasJackpot={rowHasJackpot}
              rowName={category.name[language] || category.name[DEFAULT_LANGUAGE]}
              setReferenceElement={props.setReferenceElement}
            />
          </React.Fragment>
        ))}
      </div>
      {firstChunk && firstChunk.size > 6 && (
        <button
          className={classNames(styles.arrow, styles.arrowRight, carouselRightArrowClassName)}
          onClick={() => scrollCarousel(300)}
        >
          <Chevron direction="right" />
        </button>
      )}
    </div>
  );
});
